import { useEffect, useRef, useState } from 'react';
import { apkDownloadUrl } from '../../portfolio';

const AppDownload = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const checkStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(checkStandalone);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  if (isStandalone) return null;

  return (
    <section
      id="app-download"
      ref={sectionRef}
      className="relative py-16 lg:py-24 overflow-hidden px-4 md:px-0"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-primary/90" />
      <div className="absolute inset-0 bg-dot-grid opacity-20 pointer-events-none" />
      <div className="absolute -top-24 right-10 w-96 h-96 rounded-full bg-secondary/30 blur-3xl animate-aurora pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-80 h-80 rounded-full bg-hotpink/20 blur-3xl animate-aurora pointer-events-none" style={{ animationDelay: '-5s' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-2 md:px-6 lg:px-8">
        <div
          className={`flex flex-col-reverse lg:flex-row items-center justify-between gap-12 lg:gap-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex-1 text-center lg:text-left max-w-xl">
            <span className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-semibold backdrop-blur-sm">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.523 15.341c-.507 0-.92.413-.92.92s.413.92.92.92.92-.413.92-.92-.413-.92-.92-.92zm-11.046 0c-.507 0-.92.413-.92.92s.413.92.92.92.92-.413.92-.92-.413-.92-.92-.92zm11.405-6.02l1.997-3.46a.416.416 0 00-.152-.57.416.416 0 00-.57.152l-2.022 3.502A12.02 12.02 0 0012 8.25c-1.86 0-3.61.423-5.135 1.175L4.843 5.923a.416.416 0 00-.57-.152.416.416 0 00-.152.57l1.997 3.46C2.9 11.25.75 14.355.75 17.91h22.5c0-3.555-2.15-6.66-5.368-8.589z" />
              </svg>
              Android App
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 font-space tracking-tighter-2">
              Get SkillEx on{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-mint via-skyblue to-secondary">
                Android
              </span>
            </h2>
            <p className="text-base md:text-lg text-white/70 mb-8 leading-relaxed">
              Download the SkillEx APK and take coaching, courses, and services with you. Install directly on your Android device.
            </p>
            <a
              href={apkDownloadUrl}
              download="SkillEx.apk"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-white text-slate-900 font-bold shadow-juicy hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 shine-overlay w-full sm:w-auto justify-center"
            >
              <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17.523 15.341c-.507 0-.92.413-.92.92s.413.92.92.92.92-.413.92-.92-.413-.92-.92-.92zm-11.046 0c-.507 0-.92.413-.92.92s.413.92.92.92.92-.413.92-.92-.413-.92-.92-.92zm11.405-6.02l1.997-3.46a.416.416 0 00-.152-.57.416.416 0 00-.57.152l-2.022 3.502A12.02 12.02 0 0012 8.25c-1.86 0-3.61.423-5.135 1.175L4.843 5.923a.416.416 0 00-.152.57l1.997 3.46C2.9 11.25.75 14.355.75 17.91h22.5c0-3.555-2.15-6.66-5.368-8.589z" />
              </svg>
              Download APK
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </a>
            <p className="mt-3 text-sm text-white/45">
              Direct install for Android · Enable “Install unknown apps” if prompted
            </p>
          </div>

          {/* Android phone mockup */}
          <div
            className={`relative flex-shrink-0 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <div className="absolute inset-0 scale-110 rounded-[3rem] bg-mint/20 blur-2xl pointer-events-none" />
            <div className="relative w-[220px] lg:w-[260px] mx-auto">
              {/* Device frame */}
              <div className="relative rounded-[2.4rem] bg-slate-950 p-2.5 shadow-2xl ring-1 ring-white/20">
                {/* Side buttons */}
                <div className="absolute -left-1 top-24 w-1 h-10 rounded-l bg-slate-700" />
                <div className="absolute -left-1 top-40 w-1 h-14 rounded-l bg-slate-700" />
                <div className="absolute -right-1 top-32 w-1 h-16 rounded-r bg-slate-700" />

                {/* Screen */}
                <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-b from-slate-800 to-slate-900 aspect-[9/19]">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20 w-24 h-5 bg-slate-950 rounded-b-2xl" />

                  {/* Status bar */}
                  <div className="absolute top-2 left-0 right-0 z-10 flex justify-between px-5 text-[9px] text-white/70 font-medium">
                    <span>9:41</span>
                    <span>100%</span>
                  </div>

                  {/* App screen content with logo */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center px-6 pt-8">
                    <div className="absolute inset-0 bg-gradient-mesh opacity-40" />
                    <div className="relative w-28 h-28 lg:w-32 lg:h-32 rounded-[1.75rem] overflow-hidden shadow-juicy ring-4 ring-white/20 animate-float" style={{ animationDuration: '4s' }}>
                      <img
                        src="/images/logo.png"
                        alt="SkillEx"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <p className="relative mt-5 text-white font-extrabold text-lg font-space tracking-tight">
                      SkillEx
                    </p>
                    <p className="relative text-white/50 text-xs mt-1 text-center">
                      Learn · Grow · Succeed
                    </p>
                    <div className="relative mt-6 w-full max-w-[140px] h-9 rounded-xl bg-gradient-brand flex items-center justify-center text-white text-xs font-bold shadow-soft">
                      Open App
                    </div>
                  </div>

                  {/* Home indicator */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 rounded-full bg-white/40" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppDownload;